import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Eye, EyeOff, Copy, Check, User, Key, Search, X, Pencil, Send,
  FileText, CreditCard, UserCircle, Clock, ChevronDown, ChevronUp,
  Mail, Phone, Building, MapPin, Hash
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { DecryptedVaultItem } from '@/lib/vault-crypto';
import { CipherType, FieldType } from '@/api/vault';
import { generateTOTP, formatTOTPCode, type TOTPResult } from '@/lib/totp';
import { EditItemSheet } from './EditItemSheet';
import { SendSheet } from './SendSheet';

interface VaultItemsListProps {
  items: DecryptedVaultItem[];
  hideSearch?: boolean;
}

type CopiedField = string | null;

// TOTP Display Component
function TOTPDisplay({ secret }: { secret: string }) {
  const [totp, setTotp] = useState<TOTPResult | null>(null);

  useEffect(() => {
    const updateTOTP = () => {
      try {
        setTotp(generateTOTP(secret));
      } catch (e) {
        console.error('TOTP generation failed:', e);
      }
    };

    updateTOTP();
    const interval = setInterval(updateTOTP, 1000);
    return () => clearInterval(interval);
  }, [secret]);

  const [copied, setCopied] = useState(false);

  const copyCode = useCallback(async () => {
    if (!totp) return;
    try {
      await navigator.clipboard.writeText(totp.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  }, [totp]);

  if (!totp) return null;

  const progress = (totp.remaining / totp.period) * 100;

  return (
    <div className="flex items-center gap-1 bg-muted/50 rounded px-2 py-1">
      <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="font-mono text-sm font-medium tracking-wider">
        {formatTOTPCode(totp.code)}
      </span>
      <div className="w-8 h-1.5 bg-muted rounded-full overflow-hidden ml-1">
        <div
          className={`h-full transition-all duration-1000 ${
            totp.remaining <= 5 ? 'bg-red-500' : 'bg-primary'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 ml-auto"
        onClick={copyCode}
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
    </div>
  );
}

// Get icon for cipher type
function getTypeIcon(type: CipherType) {
  switch (type) {
    case CipherType.Login:
      return <Key className="h-4 w-4" />;
    case CipherType.SecureNote:
      return <FileText className="h-4 w-4" />;
    case CipherType.Card:
      return <CreditCard className="h-4 w-4" />;
    case CipherType.Identity:
      return <UserCircle className="h-4 w-4" />;
    default:
      return <Key className="h-4 w-4" />;
  }
}

export function VaultItemsList({ items, hideSearch = false }: VaultItemsListProps) {
  const [visibleFields, setVisibleFields] = useState<Record<string, Set<string>>>({});
  const [copied, setCopied] = useState<Record<string, CopiedField>>({});
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [editingItem, setEditingItem] = useState<DecryptedVaultItem | null>(null);
  const [sendPrefill, setSendPrefill] = useState<{ text: string; name: string } | null>(null);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(item =>
      item.name?.toLowerCase().includes(q) ||
      item.username?.toLowerCase().includes(q) ||
      item.notes?.toLowerCase().includes(q) ||
      item.uris?.some(uri => uri.toLowerCase().includes(q)) ||
      item.card?.cardholderName?.toLowerCase().includes(q) ||
      item.identity?.firstName?.toLowerCase().includes(q) ||
      item.identity?.lastName?.toLowerCase().includes(q) ||
      item.identity?.email?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const toggleFieldVisibility = (itemId: string, field: string) => {
    setVisibleFields((prev) => {
      const itemFields = new Set(prev[itemId] || []);
      if (itemFields.has(field)) {
        itemFields.delete(field);
      } else {
        itemFields.add(field);
      }
      return { ...prev, [itemId]: itemFields };
    });
  };

  const isFieldVisible = (itemId: string, field: string) => {
    return visibleFields[itemId]?.has(field) ?? false;
  };

  const copyToClipboard = async (itemId: string, text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((prev) => ({ ...prev, [itemId]: field }));
      setTimeout(() => {
        setCopied((prev) => ({ ...prev, [itemId]: null }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleNotes = (itemId: string) => {
    setExpandedNotes((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Нет записей в этой коллекции
      </div>
    );
  }

  // Render field with visibility toggle and copy
  const renderSecretField = (
    itemId: string,
    fieldKey: string,
    value: string,
    icon: React.ReactNode
  ) => (
    <div className="flex items-center gap-1 min-w-0 flex-1 bg-muted/50 rounded px-2 py-1">
      {icon}
      <span className="font-mono truncate text-xs">
        {isFieldVisible(itemId, fieldKey) ? value : '••••••••'}
      </span>
      <div className="flex items-center shrink-0 ml-auto">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => toggleFieldVisibility(itemId, fieldKey)}
        >
          {isFieldVisible(itemId, fieldKey) ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => copyToClipboard(itemId, value, fieldKey)}
        >
          {copied[itemId] === fieldKey ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );

  // Render text field with copy only
  const renderTextField = (
    itemId: string,
    fieldKey: string,
    value: string,
    icon: React.ReactNode
  ) => (
    <div className="flex items-center gap-1 min-w-0 flex-1 bg-muted/50 rounded px-2 py-1">
      {icon}
      <span className="truncate text-xs">{value}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 ml-auto"
        onClick={() => copyToClipboard(itemId, value, fieldKey)}
      >
        {copied[itemId] === fieldKey ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
    </div>
  );

  // Render Login item
  const renderLogin = (item: DecryptedVaultItem) => (
    <div className="space-y-1.5">
      {/* Username & Password row */}
      <div className="flex items-center gap-1 text-xs">
        {item.username && renderTextField(
          item.id, 'username', item.username,
          <User className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        {item.password && renderSecretField(
          item.id, 'password', item.password,
          <Key className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
      </div>

      {/* TOTP */}
      {item.totp && (
        <div className="text-xs">
          <TOTPDisplay secret={item.totp} />
        </div>
      )}
    </div>
  );

  // Render Secure Note item
  const renderSecureNote = (item: DecryptedVaultItem) => (
    <div className="text-xs text-muted-foreground">
      {item.notes ? (
        <div className="whitespace-pre-wrap break-words">
          {item.notes.length > 100 && !expandedNotes[item.id]
            ? item.notes.slice(0, 100) + '...'
            : item.notes}
        </div>
      ) : (
        <span className="italic">Пустая заметка</span>
      )}
    </div>
  );

  // Render Card item
  const renderCard = (item: DecryptedVaultItem) => {
    if (!item.card) return null;
    const { cardholderName, brand, number, expMonth, expYear, code } = item.card;

    return (
      <div className="space-y-1.5 text-xs">
        {/* Brand & Cardholder */}
        <div className="flex items-center gap-2">
          {brand && <span className="font-medium">{brand}</span>}
          {cardholderName && <span className="text-muted-foreground">{cardholderName}</span>}
        </div>

        {/* Number */}
        {number && (
          <div className="flex items-center gap-1">
            {renderSecretField(
              item.id, 'cardNumber', number,
              <CreditCard className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Exp & CVV */}
        <div className="flex items-center gap-1">
          {expMonth && expYear && (
            <div className="flex items-center gap-1 bg-muted/50 rounded px-2 py-1">
              <span className="text-muted-foreground">Срок:</span>
              <span className="font-mono">{expMonth}/{expYear}</span>
            </div>
          )}
          {code && renderSecretField(
            item.id, 'cvv', code,
            <Hash className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </div>
      </div>
    );
  };

  // Render Identity item
  const renderIdentity = (item: DecryptedVaultItem) => {
    if (!item.identity) return null;
    const id = item.identity;

    // Build full name
    const nameParts = [id.title, id.firstName, id.middleName, id.lastName].filter(Boolean);
    const fullName = nameParts.join(' ');

    // Build address
    const addressParts = [
      id.address1,
      id.address2,
      id.address3,
      [id.city, id.state, id.postalCode].filter(Boolean).join(', '),
      id.country
    ].filter(Boolean);
    const fullAddress = addressParts.join('\n');

    return (
      <div className="space-y-1.5 text-xs">
        {/* Name */}
        {fullName && (
          <div className="flex items-center gap-1">
            {renderTextField(
              item.id, 'name', fullName,
              <UserCircle className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Company */}
        {id.company && (
          <div className="flex items-center gap-1">
            {renderTextField(
              item.id, 'company', id.company,
              <Building className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Email & Phone */}
        <div className="flex items-center gap-1">
          {id.email && renderTextField(
            item.id, 'email', id.email,
            <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          {id.phone && renderTextField(
            item.id, 'phone', id.phone,
            <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </div>

        {/* Address */}
        {fullAddress && (
          <div className="flex items-center gap-1">
            {renderTextField(
              item.id, 'address', fullAddress.replace(/\n/g, ', '),
              <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Sensitive fields */}
        {(id.ssn || id.passportNumber || id.licenseNumber) && (
          <div className="flex items-center gap-1 flex-wrap">
            {id.ssn && renderSecretField(
              item.id, 'ssn', id.ssn,
              <Hash className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            {id.passportNumber && renderSecretField(
              item.id, 'passport', id.passportNumber,
              <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            {id.licenseNumber && renderSecretField(
              item.id, 'license', id.licenseNumber,
              <CreditCard className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
          </div>
        )}
      </div>
    );
  };

  // Render custom fields
  const renderCustomFields = (item: DecryptedVaultItem) => {
    if (!item.fields || item.fields.length === 0) return null;

    return (
      <div className="space-y-1 pt-1 border-t border-border/50">
        {item.fields.map((field, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground min-w-0 truncate">{field.name}:</span>
            {field.type === FieldType.Hidden ? (
              renderSecretField(
                item.id, `field-${idx}`, field.value,
                <Key className="h-3 w-3 shrink-0 text-muted-foreground" />
              )
            ) : field.type === FieldType.Boolean ? (
              <span className="font-medium">{field.value === 'true' ? 'Да' : 'Нет'}</span>
            ) : (
              <span className="truncate">{field.value}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render notes section
  const renderNotes = (item: DecryptedVaultItem) => {
    // Don't show notes for SecureNote (already shown in main content)
    if (!item.notes || item.type === CipherType.SecureNote) return null;

    const isLong = item.notes.length > 100;
    const isExpanded = expandedNotes[item.id];

    return (
      <div className="pt-1.5 border-t border-border/50">
        <button
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-full text-left"
          onClick={() => toggleNotes(item.id)}
        >
          <FileText className="h-3 w-3" />
          <span>Заметки</span>
          {isLong && (
            isExpanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />
          )}
        </button>
        {(!isLong || isExpanded) && (
          <div className="mt-1 flex items-start gap-1">
            <div className="text-xs whitespace-pre-wrap break-words text-muted-foreground flex-1 select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
              {item.notes}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 mt-0.5"
              onClick={() => copyToClipboard(item.id, item.notes!, 'notes')}
            >
              {copied[item.id] === 'notes' ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const formatItemForSend = (item: DecryptedVaultItem): { text: string; name: string } => {
    switch (item.type) {
      case CipherType.Login: {
        const parts: string[] = [];
        if (item.username) parts.push(`Логин: ${item.username}`);
        if (item.password) parts.push(`Пароль: ${item.password}`);
        if (item.uris?.length) parts.push(`URL: ${item.uris[0]}`);
        if (item.notes) parts.push(`\n${item.notes}`);
        return { text: parts.join('\n'), name: item.name };
      }
      case CipherType.SecureNote:
        return { text: item.notes || '', name: item.name };
      case CipherType.Card: {
        const parts: string[] = [];
        if (item.card?.cardholderName) parts.push(`Владелец: ${item.card.cardholderName}`);
        if (item.card?.number) parts.push(`Номер: ${item.card.number}`);
        if (item.card?.expMonth && item.card?.expYear) parts.push(`Срок: ${item.card.expMonth}/${item.card.expYear}`);
        return { text: parts.join('\n'), name: item.name };
      }
      case CipherType.Identity: {
        const parts: string[] = [];
        const id = item.identity;
        if (id?.firstName || id?.lastName) parts.push([id?.firstName, id?.lastName].filter(Boolean).join(' '));
        if (id?.email) parts.push(`Email: ${id.email}`);
        if (id?.phone) parts.push(`Тел: ${id.phone}`);
        return { text: parts.join('\n'), name: item.name };
      }
      default:
        return { text: item.notes || '', name: item.name };
    }
  };

  return (
    <div className="space-y-2">
      {/* Search */}
      {!hideSearch && items.length > 3 && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск..."
            className="pl-9 pr-9 h-9"
          />
          {search && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearch('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Items */}
      {filteredItems.length === 0 ? (
        <div className="py-4 text-center text-muted-foreground text-sm">
          Ничего не найдено
        </div>
      ) : (
        filteredItems.map((item) => (
          <div
            key={item.id}
            className="p-3 rounded-lg border bg-card"
          >
            {/* Header: Type icon + Name + URI + Edit button */}
            <div className="flex items-start gap-2 mb-2">
              <div className="shrink-0 text-muted-foreground mt-0.5">
                {getTypeIcon(item.type)}
              </div>
              <div className="min-w-0 flex-1">
                <button
                  className="font-medium text-sm truncate block text-left hover:underline w-full"
                  onClick={() => setEditingItem(item)}
                >
                  {item.name}
                </button>
                {item.uris && item.uris.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="truncate">{item.uris[0].replace(/^https?:\/\//, '')}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 shrink-0"
                      onClick={() => copyToClipboard(item.id, item.uris![0], 'uri')}
                    >
                      {copied[item.id] === 'uri' ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setSendPrefill(formatItemForSend(item))}
                title="Send по ссылке"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setEditingItem(item)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Type-specific content */}
            {item.type === CipherType.Login && renderLogin(item)}
            {item.type === CipherType.SecureNote && renderSecureNote(item)}
            {item.type === CipherType.Card && renderCard(item)}
            {item.type === CipherType.Identity && renderIdentity(item)}

            {/* Custom fields */}
            {renderCustomFields(item)}

            {/* Notes (for non-SecureNote types) */}
            {renderNotes(item)}
          </div>
        ))
      )}

      {/* Edit Item Sheet */}
      <EditItemSheet
        item={editingItem}
        open={!!editingItem}
        onOpenChange={(open) => !open && setEditingItem(null)}
      />

      {/* Send Sheet */}
      <SendSheet
        open={!!sendPrefill}
        onOpenChange={(open) => !open && setSendPrefill(null)}
        prefillText={sendPrefill?.text}
        prefillName={sendPrefill?.name}
      />
    </div>
  );
}
